import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  AddOnItemName,
  OrganizationProfile,
  OrganizationUser,
  TemplateIntakeForm,
  TemplateIntakeFormStatus,
} from "schemas/dashboard";
import { SetAlertType } from "schemas/functions";

import { Template } from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";
import { titleCase } from "utils/string";
import { formatEpochTimestamp } from "utils/time";

import AddTemplateForUserDialog from "./components/AddTemplateForUserDialog";
import UserIntakeFormDialog from "./components/UserIntakeFormDialog";
import {
  AdminAddOnItem,
  BackgroundDetails,
  OnboardingReviewRequest,
  OnboardingReviewRequestStatus,
} from "./schema";

interface Props {
  setAlert: SetAlertType;
  organizationUser: OrganizationUser | null;
}

const AdminAddOns = ({ organizationUser, setAlert }: Props) => {
  const { getToken } = useAuth();
  const [userPurchases, setUserPurchases] = useState<AdminAddOnItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [openIntakeDialog, setOpenIntakeDialog] = useState(false);
  const [intakeForm, setIntakeForm] = useState<TemplateIntakeForm | null>(null);
  const [backgroundDetails, setBackgroundDetails] = useState<
    BackgroundDetails[]
  >([]);
  const [orgProfile, setOrgProfile] = useState<OrganizationProfile | null>(
    null,
  );

  const [editTemplateId, setEditTemplateId] = useState<number | undefined>(
    undefined,
  );
  const [addTemplateIntakeFormId, setIntakeFormId] = useState<number | null>(
    null,
  );
  const [emailLoading, setEmailLoading] = useState(false);
  const [onboardingReviewRequest, setOnboardingReviewRequest] =
    useState<OnboardingReviewRequest | null>(null);
  const [onboardingReviewLoading, setOnboardingReviewLoading] = useState(false);
  const [onboardingReviewUpdating, setOnboardingReviewUpdating] =
    useState(false);

  const fetchUserPurchases = async () => {
    if (!organizationUser) return;
    try {
      setLoading(true);
      const res = await fetcherAuth(
        getToken,
        `/api/admin/organizations/${organizationUser?.organizationId}/add-ons`,
        "GET",
      );
      setUserPurchases(res.userPurchases);
    } catch (error) {
      setAlert(error.message || "Unable to fetch user purchases", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchOnboardingReviewRequest = async () => {
    if (!organizationUser) return;
    try {
      setOnboardingReviewLoading(true);
      const res = await fetcherAuth(
        getToken,
        `/api/admin/organizations/${organizationUser.organizationId}/onboarding-review-request`,
        "GET",
      );
      setOnboardingReviewRequest(res?.onboardingReviewRequest ?? null);
    } catch (error) {
      setAlert(
        error.message || "Unable to fetch onboarding review request",
        "error",
      );
    } finally {
      setOnboardingReviewLoading(false);
    }
  };

  const toggleOnboardingReviewStatus = async () => {
    if (!organizationUser || !onboardingReviewRequest) return;
    const nextStatus =
      onboardingReviewRequest.status === OnboardingReviewRequestStatus.fulfilled
        ? OnboardingReviewRequestStatus.pending
        : OnboardingReviewRequestStatus.fulfilled;
    try {
      setOnboardingReviewUpdating(true);
      const res = await fetcherAuth(
        getToken,
        `/api/admin/organizations/${organizationUser.organizationId}/onboarding-review-request`,
        "PUT",
        {},
        { status: nextStatus },
      );
      setOnboardingReviewRequest(res?.onboardingReviewRequest ?? null);
      setAlert(
        nextStatus === OnboardingReviewRequestStatus.fulfilled
          ? "Marked as fulfilled."
          : "Marked as pending.",
        "success",
      );
    } catch (error) {
      setAlert(
        error.message || "Unable to update onboarding review request",
        "error",
      );
    } finally {
      setOnboardingReviewUpdating(false);
    }
  };

  const fetchUserBackgroundDetails = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/admin/organizations/${organizationUser?.organizationId}/background-details`,
        "GET",
      );
      setBackgroundDetails(res.backgroundDetails);
      setOrgProfile(res.organizationProfile);
    } catch (error) {}
  };

  const handleTemplateClick = (template: Template) => {
    if (!template?.intakeFormId) return;
    setIntakeFormId(template.intakeFormId);
    setEditTemplateId(template.id);
  };

  useEffect(() => {
    if (organizationUser?.id) {
      fetchUserBackgroundDetails();
      fetchUserPurchases();
      fetchOnboardingReviewRequest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationUser?.id]);

  const getTemplates = () => {
    let templates: Template[] = [];
    userPurchases.forEach((purchase) => {
      purchase.templateIntakeForms?.forEach((form) => {
        if (form.templates && form.templates.length > 0) {
          templates = templates.concat(form.templates);
        }
      });
    });
    return templates;
  };

  const sendTemplateCompletedEmail = async () => {
    try {
      setEmailLoading(true);
      await fetcherAuth(
        getToken,
        `/api/admin/organizations/${organizationUser?.organizationId}/add-ons/send-completed-template-email`,
        "POST",
      );
      setAlert("Successfully sent email.", "success");
      fetchUserPurchases();
    } catch (error) {
      setAlert(error.message || "Unable to send email", "error");
    } finally {
      setEmailLoading(false);
    }
  };

  const renderOnboardingReviewCard = () => {
    if (onboardingReviewLoading) {
      return (
        <Paper sx={{ p: 2, mt: 2, mb: 2, maxWidth: 1300 }}>
          <CircularProgress size={20} />
        </Paper>
      );
    }

    if (!onboardingReviewRequest || onboardingReviewRequest.isArchived) {
      return null;
    }

    const isFulfilled =
      onboardingReviewRequest.status ===
      OnboardingReviewRequestStatus.fulfilled;

    return (
      <Paper sx={{ p: 2, mt: 2, mb: 2, maxWidth: 1300 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
        >
          <Box>
            <Typography sx={{ fontWeight: 600 }}>
              Onboarding Review Request
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Plan: {onboardingReviewRequest.planName}
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center" gap={1}>
            <Chip
              size="small"
              label={isFulfilled ? "Fulfilled" : "Pending"}
              color={isFulfilled ? "success" : "warning"}
            />
            <LoadingButton
              size="small"
              variant="contained"
              disableElevation
              loading={onboardingReviewUpdating}
              onClick={toggleOnboardingReviewStatus}
            >
              {isFulfilled ? "Mark as Pending" : "Mark as Fulfilled"}
            </LoadingButton>
          </Stack>
        </Stack>
      </Paper>
    );
  };

  return (
    <>
      <Typography variant="h6">Add-Ons Purchases</Typography>

      {renderOnboardingReviewCard()}

      <LoadingButton
        size="small"
        variant="contained"
        disableElevation
        loading={emailLoading}
        sx={{ mb: 1 }}
        onClick={sendTemplateCompletedEmail}
      >
        Send Template Completed Email
      </LoadingButton>

      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper} sx={{ maxWidth: 1300, mt: 3 }}>
          <Table size="small" aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: 14, fontWeight: 600 }}>
                  Date Purchased
                </TableCell>
                <TableCell sx={{ fontSize: 14, fontWeight: 600 }}>
                  Item Name
                </TableCell>
                <TableCell sx={{ fontSize: 14, fontWeight: 600 }}>
                  Quantity
                </TableCell>
                <TableCell sx={{ fontSize: 14, fontWeight: 600 }}>
                  Purchase Details
                </TableCell>
                <TableCell sx={{ fontSize: 14, fontWeight: 600 }}>
                  Added Templates
                </TableCell>
                <TableCell sx={{ fontSize: 14, fontWeight: 600 }}>
                  Domain Info
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {userPurchases.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    {formatEpochTimestamp({
                      epochTimestamp: row?.createdAt,
                      fullDate: true,
                    })}
                  </TableCell>
                  <TableCell>{titleCase(row.itemName)}</TableCell>
                  <TableCell>{row.quantity}</TableCell>
                  <TableCell>
                    {row.itemName === AddOnItemName.pitching_templates ? (
                      <Grid>
                        {row?.templateIntakeForms?.map((form, index) => (
                          <Box key={index}>
                            Template {index + 1} is{" "}
                            {form.status ===
                            TemplateIntakeFormStatus.pending ? (
                              <>pending 🟡</>
                            ) : (
                              <>completed ✅</>
                            )}
                            <br />{" "}
                            <Grid container gap={1} sx={{ mb: 2, mt: 1 }}>
                              <Button
                                sx={{
                                  textTransform: "none",
                                  cursor: "pointer",
                                  fontSize: 12,
                                  textDecoration: "none",
                                }}
                                onClick={() => {
                                  setOpenIntakeDialog(true);
                                  setIntakeForm(form);
                                }}
                                variant="outlined"
                                size="small"
                              >
                                View User Intake{" "}
                              </Button>
                              <Button
                                onClick={() => {
                                  if (form.id) setIntakeFormId(form.id);
                                  setEditTemplateId(undefined);
                                }}
                                sx={{
                                  color: "purple.dark",
                                  fontSize: 12,
                                  cursor: "pointer",
                                  textDecoration: "none",
                                  textTransform: "none",
                                }}
                                size="small"
                              >
                                Add Template
                              </Button>
                            </Grid>
                          </Box>
                        ))}
                      </Grid>
                    ) : (
                      <>--</>
                    )}
                  </TableCell>
                  <TableCell>
                    {getTemplates() && getTemplates().length > 0 ? (
                      <>
                        {getTemplates().map((template: Template) => (
                          <Link
                            key={template.id}
                            sx={{ cursor: "pointer" }}
                            onClick={() => handleTemplateClick(template)}
                          >
                            {template.name} <br />
                          </Link>
                        ))}
                      </>
                    ) : (
                      "--"
                    )}
                  </TableCell>
                  <TableCell>
                    {row?.domainInfo ? (
                      <>
                        Email: {row.domainInfo.username}@{row.domainInfo.domain}
                        <br />
                        First Name: {row.domainInfo.first_name}
                        <br />
                        Last Name: {row.domainInfo.last_name}
                      </>
                    ) : (
                      "--"
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <UserIntakeFormDialog
        openIntakeDialog={openIntakeDialog}
        setOpenIntakeDialog={setOpenIntakeDialog}
        intakeForm={intakeForm}
        backgroundDetails={backgroundDetails}
        orgProfile={orgProfile}
        setIntakeForm={setIntakeForm}
      />

      {addTemplateIntakeFormId && (
        <AddTemplateForUserDialog
          open={Boolean(addTemplateIntakeFormId)}
          handleClose={() => setIntakeFormId(null)}
          templateIntakeFormId={addTemplateIntakeFormId}
          setAlert={setAlert}
          fetchUserPurchases={fetchUserPurchases}
          editTemplateId={editTemplateId}
        />
      )}
    </>
  );
};

export default AdminAddOns;
